import { Markdown } from "./Markdown";

const DEFAULT_COLUMN_WIDTH = { wpx: 100 };
const DEFAULT_ROW_HEIGHT = { hpx: 20 };

/**
 * Фрагмент Excel-таблицы.
 */
export default class TableFragment {
  /**
   * @param {[number, number]} dim - размеры фрагмента (число клеток по ширине и высоте)
   * @param {object} frag - данные, стили и разметка
   * @param {unknown[][]} frag.data - матрица с данными
   * @param {Markdown[]} frag.markdown - массив стилей разметки
   * @param {number[]} [frag.columnWidth] - массив ширин
   * @param {number[]} [frag.rowHeight] - массив высот
   */
  constructor([n, m], { data, markdown, columnWidth, rowHeight }) {
    if (!columnWidth) columnWidth = new Array(n).fill(DEFAULT_COLUMN_WIDTH);
    if (!rowHeight) rowHeight = new Array(m).fill(DEFAULT_ROW_HEIGHT);
    this.n = n;
    this.m = m;
    this.data = data;
    this.markdown = markdown;
    this.columnWidth = columnWidth;
    this.rowHeight = rowHeight;
    TableFragment.validate(this);
  }

  static fromJSON({ n, m, data, markdown = [], columnWidth, rowHeight }) {
    return new TableFragment([n, m], { data, markdown: markdown.map(Markdown.fromJSON), columnWidth, rowHeight });
  }

  static validate({ n, m, data, markdown, columnWidth, rowHeight }) {
    if (!Array.isArray(data)) throw new Error('data must be an array');
    if (data.length !== m) throw new Error(`data.length=${data.length} is expected to be ${m}`);
    data.forEach((row, i) => {
      if (row.length !== n) throw new Error(`data[i].length=${data[i].length} is expected to be ${n}`);
    });
    if (!Array.isArray(markdown)) throw new Error(`markdown is not an array`);
    markdown.forEach((md, i) => {
      if (md instanceof Markdown) return;
      try {
        markdown[i] = Markdown.fromJSON(md);
      } catch (e) {
        throw new Error(`Failed to convert markdown[${i}]: ${e.message}`);
      }
      // TODO: check markdown to affect the range [0..n]*[0..m]
    });
    if (!Array.isArray(columnWidth)) throw new Error('columnWidth must be an array');
    if (columnWidth.length !== n) throw new Error(`columnWidth.length=${columnWidth.length} is expected to be ${n}`);
    if (!Array.isArray(rowHeight)) throw new Error('rowHeight must be an array');
    if (rowHeight.length !== m) throw new Error(`rowHeight.length=${rowHeight.length} is expected to be ${m}`);
  }

  /**
   * Пустой фрагмент указанного размера.
   * @param {[number, number]} dim - размеры фрагмента
   * @return {TableFragment}
   */
  static empty([n, m]) {
    return new TableFragment([n, m], {
      data: new Array(m).fill(0).map(() => new Array(n).fill(0).map(() => undefined)),
      markdown: [],
      columnWidth: undefined,
      rowHeight: undefined,
    });
  }

  /**
   * Создать фрагмент на основе прямоугольной таблицы с данными.
   * @param {unknown[][]} data
   * @return {TableFragment}
   */
  static plainData(data) {
    if (data.length === 0) return TableFragment.empty([0, 0]);
    const n = data[0].length;
    const m = data.length;
    return new TableFragment([n, m], {
      data,
      markdown: [],
      columnWidth: undefined,
      rowHeight: undefined,
    });
  }

  /**
   * Поместить меньший фрагмент как часть текущего.
   * @param {TableFragment} frag
   * @param {number[]} offset - положение ячейки 0,0 вставляемого фрагмента
   * @return {TableFragment} this
   */
  place(frag, [x0, y0]) {
    // TODO: Нормальные сообщения для диагностики ошибок
    if (frag.n + x0 > this.n) throw new Error('out of range');
    if (frag.m + y0 > this.m) throw new Error('out of range');
    for (let dx = 0; dx < frag.n; ++dx) {
      for (let dy = 0; dy < frag.m; ++dy) {
        this.data[y0 + dy][x0 + dx] = frag.data[dy][dx];
      }
    }
    this.markdown.push(...frag.markdown.map((md) => md.shift(x0, y0)));
    for (let dx = 0; dx < frag.n; ++dx) {
      if (frag.columnWidth[dx] === undefined)
        continue;
      if (JSON.stringify(this.columnWidth[x0 + dx]) !== JSON.stringify(frag.columnWidth[dx]))
        throw new Error('columnWidth mismatches');
      this.columnWidth[x0 + dx] = frag.columnWidth[dx];
    }
    for (let dy = 0; dy < frag.m; ++dy) {
      if (frag.rowHeight[dy] === undefined)
        continue;
      if (JSON.stringify(this.rowHeight[y0 + dy]) !== JSON.stringify(frag.rowHeight[dy]))
        throw new Error('rowHeight mismatches');
      this.rowHeight[y0 + dy] = frag.rowHeight[dy];
    }
  }

  /**
   * Расширить фрагммент до указанного размера.
   * Пустые ячейки добавляются слева и снизу.
   * @param {[number, number]} dim - размеры фрагмента (число клеток по ширине и высоте)
   * @return {TableFragment}
   */
  pad([n, m]) {
    if (n < this.n || m < this.m) throw new Error('Cannot shrink the fragment');
    return new TableFragment([n, m], {
      data: this.data.map((row) => row.concat(new Array(n - this.n).fill(''))).concat(new Array(m - this.m).fill(new Array(n).fill(''))),
      markdown: this.markdown,
      columnWidth: this.columnWidth.concat(new Array(n - this.n).fill(DEFAULT_COLUMN_WIDTH)),
      rowHeight: this.rowHeight.concat(new Array(m - this.m).fill(DEFAULT_ROW_HEIGHT)),
    });
  }
}
